import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { designAPI } from '@api/designs';
import { setCategories, setCoverages, setManufacturers, setModels } from '@features/redux/slices/designSlice';
import { store } from '@features/redux/store';
import { useAlert } from './useAlert';

import type { RootState } from '@features/redux/store';

export const useLoadDesignData = () => {
    const { IdToken } = useSelector((state: RootState) => state.auth);
    const { setAlert } = useAlert();

    const { manufacturers, models, coverages, categories } = useSelector((state: RootState) => state.design);

    const [loadingCoverage, setLoadingCoverage] = useState<boolean>(false);
    const [loadingModel, setLoadingModel] = useState<boolean>(false);
    const [loadingManufacturer, setLoadingManufacturer] = useState<boolean>(false);
    const [loadingCategory, setLoadingCategory] = useState<boolean>(false);

    const { getCoverages, getModels, getManufacturers, getCategories } = designAPI(IdToken as string);

    const loadManufacturers = async (force?: boolean) => {
        try {
            if (!manufacturers.length || force) {
                setLoadingManufacturer(true);
                const manufacturers = (await getManufacturers()) || [];
                store.dispatch(setManufacturers(manufacturers));
            }
        } catch (error) {
            setAlert('Failed to load manufacturers. Please try again.', 'error');
        } finally {
            setLoadingManufacturer(false);
        }
    };

    const loadCategories = async (force?: boolean) => {
        try {
            if (!categories.length || force) {
                setLoadingCategory(true);
                const categories = (await getCategories()) || [];
                store.dispatch(setCategories(categories));
            }
        } catch (error) {
            setAlert('Failed to load categories. Please try again.', 'error');
        } finally {
            setLoadingCategory(false);
        }
    };

    const loadCoverages = async (force?: boolean) => {
        try {
            if (!coverages.length || force) {
                setLoadingCoverage(true);
                const coverages = (await getCoverages('eng')) || [];
                store.dispatch(setCoverages(coverages));
            }
        } catch (error) {
            setAlert('Failed to load coverages. Please try again.', 'error');
        } finally {
            setLoadingCoverage(false);
        }
    };

    const loadModels = async (force?: boolean) => {
        try {
            if (!models.length || force) {
                setLoadingModel(true);
                const models = (await getModels()) || [];
                store.dispatch(setModels(models));
            }
        } catch (error) {
            setAlert('Failed to load models. Please try again.', 'error');
        } finally {
            setLoadingModel(false);
        }
    };

    useEffect(() => {
        loadManufacturers();
        loadCoverages();
        loadModels();
        loadCategories();
    }, []);

    return {
        manufacturers,
        models,
        coverages,
        categories,
        loadCoverages,
        loadModels,
        loadManufacturers,
        loadCategories,
        loadingCategory,
        loadingCoverage,
        loadingManufacturer,
        loadingModel,
    };
};
